import Tooltip from 'components/@extended/Tooltip';
import { GLYPH_OWN_SIZE } from 'components/buttons/buttonStyle';
import { Autorenew as AutorenewIcon } from 'components/icons';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import { Button } from 'components/ui/button';
import { formatToHours } from 'components/util/HoursDisplay';
import { useCallback, useMemo } from 'react';
import { Control, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';
import { useSelector } from 'store';
import { FeatureFlag } from 'types/features';
import { InvoiceMatterForm, InvoiceMatterSummary } from 'types/invoice-details';
import { InvoiceMatterDetailsDto } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { paymentCurrencyInputClassName } from 'utils/usePaymentCurrencyMode';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import CapFeeBudgetBox from '../../../components/CapFeeBudgetBox';
import GreyBox, { GreyBoxes } from '../../../components/GreyBox';
import { CloseOnSendTick } from '../closeOnSend/CloseOnSendTick';

/** A grey tile laid out as one row: its title on the left, its value on the right. */
const ROW_TILE = 'flex-row items-center justify-between';

interface CapLimitArgs {
    control: Control<InvoiceMatterForm>;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    summaryData: InvoiceMatterSummary;
    isVatExemptExcludedFromDiscount?: boolean;
    isPaymentCurrencyMode?: boolean;
}

/**
 * The case's invoice cap limit and what it sets: a limit spreads into the three adjustments, over the fees alone or,
 * with Include expenses, over the expenses too. The inputs call it as they change; saving is theirs.
 */
export const useCapLimitAdjustments = ({
    control,
    setValue,
    summaryData,
    isVatExemptExcludedFromDiscount = false,
    isPaymentCurrencyMode,
}: CapLimitArgs) => {
    const { calculationLog } = summaryData;
    const log = isPaymentCurrencyMode ? calculationLog?.paymentCurrency : calculationLog;
    const totalBeforeMatterAdjustments = log?.total?.totalBeforeMatterAdjustments ?? 0;
    const feesTotalBeforMatterAdjustments = log?.fees?.totalBeforeMatterAdjustments ?? 0;

    const feesForCappedAssignments = (summaryData.invoiceAssignments || []).reduce(
        (acc, item) => acc + (item.capAdjustment ?? 0),
        0
    );
    const feesBeforeAdjustments = (summaryData.invoiceAssignments || [])
        .filter((item) => item.capAdjustment === null)
        .reduce((acc, item) => acc + (item.totalBeforeAdjustments ?? 0), 0);

    const expensesWithVatBeforeAdjustments = log?.expensesWithVat?.totalBeforeMatterAdjustments ?? 0;
    const expensesWithoutVatBeforeAdjustments = log?.expensesWithoutVat?.totalBeforeMatterAdjustments ?? 0;

    const totalBeforeAdjustments = isVatExemptExcludedFromDiscount
        ? expensesWithVatBeforeAdjustments + feesBeforeAdjustments
        : expensesWithVatBeforeAdjustments + expensesWithoutVatBeforeAdjustments + feesBeforeAdjustments;

    const proportionFee = totalBeforeAdjustments === 0 ? 0 : feesBeforeAdjustments / totalBeforeAdjustments;
    const proportionExpensesWithoutVat =
        totalBeforeAdjustments === 0 ? 0 : expensesWithoutVatBeforeAdjustments / totalBeforeAdjustments;
    const proportionExpensesWithVat =
        totalBeforeAdjustments === 0 ? 0 : expensesWithVatBeforeAdjustments / totalBeforeAdjustments;

    const [areCostsCapped, capLimitValue] = useWatch({ control, name: ['areCostsCapped', 'adjustmentAmount'] });

    const isCapLimitUndefined = useMemo(() => `${capLimitValue}` === '' || capLimitValue === null, [capLimitValue]);

    const setValuesProportionally = useCallback(
        (groupAdjustment: number) => {
            setValue(
                'adjustmentAmountForExpensesWithVat',
                Math.round((groupAdjustment * proportionExpensesWithVat - expensesWithVatBeforeAdjustments) * 100) / 100
            );
            setValue(
                'adjustmentAmountForExpensesWithoutVat',
                isVatExemptExcludedFromDiscount
                    ? null
                    : Math.round(
                          (groupAdjustment * proportionExpensesWithoutVat - expensesWithoutVatBeforeAdjustments) * 100
                      ) / 100
            );
            setValue(
                'adjustmentAmountForFees',
                Math.round((groupAdjustment * proportionFee - feesBeforeAdjustments) * 100) / 100
            );
        },
        [
            expensesWithVatBeforeAdjustments,
            expensesWithoutVatBeforeAdjustments,
            isVatExemptExcludedFromDiscount,
            feesBeforeAdjustments,
            proportionExpensesWithVat,
            proportionExpensesWithoutVat,
            proportionFee,
            setValue,
        ]
    );

    const handleOnCapLimitChange = useCallback(
        (value: string | number | undefined | null) => {
            if (!value) {
                setValue('adjustmentAmountForExpensesWithVat', null);
                setValue('adjustmentAmountForExpensesWithoutVat', null);
                setValue('adjustmentAmountForFees', null);
                return;
            }

            const threshold = areCostsCapped ? totalBeforeMatterAdjustments : feesTotalBeforMatterAdjustments;
            const appliedCap = Math.min(Number(value), threshold);
            const groupAdjustment =
                appliedCap - feesForCappedAssignments < 0 ? 0 : appliedCap - feesForCappedAssignments;
            if (areCostsCapped) {
                setValuesProportionally(groupAdjustment);
            } else {
                setValue('adjustmentAmountForExpensesWithVat', null);
                setValue('adjustmentAmountForExpensesWithoutVat', null);
                setValue('adjustmentAmountForFees', Math.round((groupAdjustment - feesBeforeAdjustments) * 100) / 100);
            }
        },
        [
            totalBeforeMatterAdjustments,
            feesTotalBeforMatterAdjustments,
            areCostsCapped,
            feesBeforeAdjustments,
            feesForCappedAssignments,
            setValue,
            setValuesProportionally,
        ]
    );

    /** Include expenses ticked or not: false while there is no limit to spread, when nothing changes. */
    const applyIncludeExpenses = useCallback(
        (checked: boolean) => {
            if (!capLimitValue && capLimitValue !== 0) {
                return false;
            }

            const groupAdjustment =
                capLimitValue - feesForCappedAssignments < 0 ? 0 : capLimitValue - feesForCappedAssignments;

            if (checked) {
                setValuesProportionally(groupAdjustment);
            } else {
                setValue('adjustmentAmountForExpensesWithVat', null);
                setValue('adjustmentAmountForExpensesWithoutVat', null);
                setValue('adjustmentAmountForFees', Math.round((groupAdjustment - feesBeforeAdjustments) * 100) / 100);
            }
            return true;
        },
        [capLimitValue, feesBeforeAdjustments, feesForCappedAssignments, setValue, setValuesProportionally]
    );

    return {
        capLimitValue,
        isCapLimitUndefined,
        handleOnCapLimitChange,
        applyIncludeExpenses,
        feesForCappedAssignments,
    };
};

/** The hours the case's timecards used, to the hundredth. */
export const getUsedHours = (summaryData: InvoiceMatterSummary) =>
    Math.round(
        summaryData.invoiceTimecards.reduce(
            (acc, invoiceTimecard) => acc + (invoiceTimecard.calculationLog?.usedTimeInHours ?? 0),
            0
        ) * 100
    ) / 100;

const CapLimitGreyBox = ({
    isInViewMode,
    control,
    onSubmit,
    setValue,
    getValues,
    summaryData,
    className,
    isVatExemptExcludedFromDiscount = false,
    isPaymentCurrencyMode,
}: {
    isInViewMode: boolean;
    control: Control<InvoiceMatterForm>;
    onSubmit: () => void;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    summaryData: InvoiceMatterSummary;
    className?: string;
    isVatExemptExcludedFromDiscount?: boolean;
    isPaymentCurrencyMode?: boolean;
}) => {
    const { buttonsDisabled: areButtonsDisabled } = useSelector((state) => state.buttons);
    const { capLimitValue, isCapLimitUndefined, handleOnCapLimitChange, applyIncludeExpenses } = useCapLimitAdjustments(
        {
            control,
            setValue,
            summaryData,
            isVatExemptExcludedFromDiscount,
            isPaymentCurrencyMode,
        }
    );

    const handleOnCheckBoxChange = useCallback(
        (checked: boolean) => {
            if (applyIncludeExpenses(checked)) {
                onSubmit();
            }
        },
        [applyIncludeExpenses, onSubmit]
    );

    const renewIcon = useMemo(
        () => (
            <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t('invoices.details.cap-limit')}
                disabled={isInViewMode}
                className={cn(
                    GLYPH_OWN_SIZE,
                    'bg-paper border-divider hover:bg-grey-100 ml-2 size-[30px] rounded-[4px] border'
                )}
                onClick={() => {
                    const capLimit = getValues('adjustmentAmount');
                    handleOnCapLimitChange(capLimit);
                    onSubmit();
                }}
            >
                <AutorenewIcon />
            </Button>
        ),
        [getValues, handleOnCapLimitChange, onSubmit, isInViewMode]
    );

    return (
        <Tooltip text="invoices.details.case-cap-adjustment-tooltip">
            <GreyBoxes className={className}>
                <div className="flex flex-row items-center justify-between">
                    <p className="text-fg-secondary text-[12px] leading-[28px]">{t('invoices.details.cap-limit')}</p>
                    <div className="flex flex-row">
                        <DecimalInput
                            name="adjustmentAmount"
                            control={control}
                            disabled={isInViewMode || areButtonsDisabled}
                            noReadOnlyDivider
                            wrapperClassName={cn(
                                'bg-paper max-w-[80px]',
                                isPaymentCurrencyMode && paymentCurrencyInputClassName
                            )}
                            onChange={(e) => handleOnCapLimitChange(e.target.value)}
                            onBlur={() => {
                                if (
                                    (!isCapLimitUndefined && Number(capLimitValue) !== summaryData.adjustmentAmount) ||
                                    (isCapLimitUndefined && summaryData.adjustmentAmount !== null)
                                ) {
                                    onSubmit();
                                }
                            }}
                            onKeyDown={(e: { key: string }) => {
                                if (e.key === 'Enter') {
                                    onSubmit();
                                }
                            }}
                        />{' '}
                        {renewIcon}
                    </div>
                </div>

                <div className="flex flex-row justify-end">
                    <CheckboxInput
                        label="invoices.details.include-expenses"
                        control={control}
                        name="areCostsCapped"
                        disabled={isInViewMode || areButtonsDisabled}
                        onChange={handleOnCheckBoxChange}
                    />
                </div>
            </GreyBoxes>
        </Tooltip>
    );
};

type Props = {
    control: Control<InvoiceMatterForm>;
    summaryData: InvoiceMatterSummary;
    invoiceId: number;
    invoiceMatter: InvoiceMatterDetailsDto;
    onSubmit: () => void;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    isInViewMode: boolean;
    isPaymentCurrencyMode?: boolean;
    displayCurrency: string;
};

export const SummaryHeaderLeftSide = ({
    control,
    summaryData,
    invoiceId,
    invoiceMatter,
    onSubmit,
    setValue,
    getValues,
    isInViewMode,
    isPaymentCurrencyMode,
    displayCurrency,
}: Props) => {
    const { calculationLog } = summaryData;
    const { displayDecimalScale } = useTimecardPrecision();
    const totalBilledHours = calculationLog?.fees?.billedTimeInHours ?? 0;
    const totalUsedHours = getUsedHours(summaryData);

    const isVatExemptExcludedFromDiscount = useIsFeatureEnabled(FeatureFlag.EXCLUDE_VAT_EXEMPT_FROM_DISCOUNT);

    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-row flex-wrap gap-2">
                <GreyBox
                    key="used-hours"
                    title={t('invoices.details.used-hours')}
                    value={formatToHours(totalUsedHours, displayDecimalScale)}
                    className={ROW_TILE}
                />
                <GreyBox
                    key="billed-hours"
                    title={t('invoices.details.billed-hours')}
                    value={formatToHours(totalBilledHours, displayDecimalScale)}
                    className={ROW_TILE}
                />
            </div>
            {summaryData.feeArrangementTypeCode === 'HOURLY_CAPPED' ? (
                <CapFeeBudgetBox
                    className="text-fg-secondary wrap-break-word"
                    variant="solid"
                    budget={summaryData.matterBillingSummary?.budget?.capTotal ?? 0}
                    remaining={summaryData.matterBillingSummary?.budget?.remainingCap ?? 0}
                    used={summaryData.matterBillingSummary?.budget?.usedCap ?? 0}
                    remainingInvoice={summaryData.matterBillingSummary?.budget?.remainingInvoicedOnly ?? 0}
                    currency={displayCurrency}
                    isInvoice
                    capInput={
                        <CapLimitGreyBox
                            onSubmit={onSubmit}
                            setValue={setValue}
                            getValues={getValues}
                            summaryData={summaryData}
                            isInViewMode={isInViewMode}
                            control={control}
                            className="px-0 pt-2 pb-0"
                            isVatExemptExcludedFromDiscount={isVatExemptExcludedFromDiscount}
                            isPaymentCurrencyMode={isPaymentCurrencyMode}
                        />
                    }
                />
            ) : (
                <CapLimitGreyBox
                    onSubmit={onSubmit}
                    setValue={setValue}
                    getValues={getValues}
                    summaryData={summaryData}
                    isInViewMode={isInViewMode}
                    control={control}
                    isVatExemptExcludedFromDiscount={isVatExemptExcludedFromDiscount}
                    isPaymentCurrencyMode={isPaymentCurrencyMode}
                />
            )}
            <CloseOnSendTick
                invoiceId={invoiceId}
                matterId={invoiceMatter.matterId}
                isConfidential={!!invoiceMatter.isConfidential}
                closeOnSend={invoiceMatter.closeOnSend}
            />
        </div>
    );
};
